import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../services/api';
import { Button } from '../components/ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
// Server returns relative URLs like /api/uploads/images/... — make them absolute for <img>
const toAbsUrl = (url) => url?.startsWith('/api/uploads/') ? `${API_BASE_URL}${url}` : (url || '');
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Plus, Save, Eye, GripVertical, Trash2, Palette, Image, Upload, X, Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import { FIELD_TYPES, FIELD_CATEGORIES, createDefaultField } from '../utils/formFieldTypes';
import FieldEditor from '../components/FormBuilder/FieldEditor';
import FormPreview from '../components/FormBuilder/FormPreview';
import FormulaBuilder from '../components/FormBuilder/FormulaBuilder';
import FieldRules from '../components/FormBuilder/FieldRules';
import PageRules from '../components/FormBuilder/PageRules';
import Integrations from '../components/FormBuilder/Integrations';
import { FORM_TEMPLATES, applyTemplate } from '../data/formTemplates';
import ImageUploader from '../components/ui/ImageUploader';

// Sortable Field Item Component
const SortableFieldItem = ({ field, isSelected, onSelect, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center space-x-3 p-4 border border-slate-200 rounded-lg transition-all duration-200 ${
        isSelected
          ? 'border-primary bg-primary-subtle'
          : 'hover:border-slate-300'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-5 h-5 text-slate-400" />
      </div>
      <div className="flex-1 cursor-pointer" onClick={onSelect}>
        <p className="font-jakarta font-medium text-slate-900">
          {field.label || `${field.type} field`}
        </p>
        <p className="text-xs text-slate-500 font-jakarta">
          {field.type} {field.required && '• Required'}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        data-testid={`delete-field-${field.id}`}
        className="hover:bg-accent-subtle hover:text-accent"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

const FormBuilder = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [basePrice, setBasePrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('fields');
  const [fieldSearch, setFieldSearch] = useState('');

  // Advanced features state
  const [pricingRules, setPricingRules] = useState([]);
  const [pricingFormula, setPricingFormula] = useState('');
  const [fieldRules, setFieldRules] = useState([]);
  const [pageBreaks, setPageBreaks] = useState([]);
  const [pageRules, setPageRules] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  
  // Form design state
  const [headerImage, setHeaderImage] = useState('');
  const [formTemplateId, setFormTemplateId] = useState('');
  const [formTheme, setFormTheme] = useState(null);

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campaignRes, formRes] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/campaigns/${campaignId}/form`)
      ]);

      setCampaign(campaignRes.data);
      
      if (formRes.data) {
        setFields(formRes.data.fields || []);
        setBasePrice(formRes.data.base_price || 0);
        setPricingRules(formRes.data.pricing_rules || []);
        setPricingFormula(formRes.data.pricing_formula || '');
        setFieldRules(formRes.data.field_rules || []);
        setPageBreaks(formRes.data.page_breaks || []);
        setPageRules(formRes.data.page_rules || []);
        setIntegrations(formRes.data.integrations || []);
        setHeaderImage(formRes.data.header_image || '');
        setFormTemplateId(formRes.data.form_template_id || '');
        setFormTheme(formRes.data.theme || null);
      }
    } catch (error) {
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);

    const updatedFields = arrayMove(fields, oldIndex, newIndex).map((field, index) => ({
      ...field,
      order: index
    }));

    setFields(updatedFields);
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const addField = (type) => {
    const newField = createDefaultField(type);
    newField.order = fields.length;
    setFields([...fields, newField]);
    setSelectedField(newField);
    toast.success('Field added');
  };

  const updateField = (updatedField) => {
    setFields(fields.map(f => f.id === updatedField.id ? updatedField : f));
    setSelectedField(updatedField);
  };

  const deleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (selectedField?.id === fieldId) {
      setSelectedField(null);
    }
    toast.success('Field deleted');
  };

  // Helper to generate value from label
  const generateValueFromLabel = (label) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/(^_|_$)/g, '') || `option_${Date.now()}`;
  };

  // Sanitize fields to ensure all options have valid 'value' fields
  const sanitizeFields = (fieldsToSanitize) => {
    return fieldsToSanitize.map(field => {
      if (field.options && field.options.length > 0) {
        return {
          ...field,
          options: field.options.map((opt, idx) => ({
            ...opt,
            value: opt.value || generateValueFromLabel(opt.label || `option_${idx}`)
          }))
        };
      }
      return field;
    });
  };

  // Validate fields before saving
  const validateFields = () => {
    const choiceFieldTypes = ['dropdown', 'radio', 'checkbox'];
    const errors = [];
    
    fields.forEach((field, index) => {
      if (choiceFieldTypes.includes(field.type)) {
        if (!field.options || field.options.length === 0) {
          errors.push(`"${field.label || field.type}" field requires at least one option.`);
        }
      }
    });
    
    return errors;
  };

  const saveForm = async () => {
    // Validate fields first
    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }

    setSaving(true);
    try {
      // Sanitize fields before saving
      const sanitizedFields = sanitizeFields(fields);
      
      await api.put(`/campaigns/${campaignId}/form`, {
        fields: sanitizedFields,
        base_price: parseFloat(basePrice) || 0,
        pricing_rules: pricingRules,
        pricing_formula: pricingFormula,
        field_rules: fieldRules,
        page_breaks: pageBreaks,
        page_rules: pageRules,
        integrations: integrations,
        header_image: headerImage || null,
        form_template_id: formTemplateId || null,
        theme: formTheme
      });
      toast.success('Form saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  // Apply a form template
  const handleApplyTemplate = (templateId) => {
    const template = FORM_TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    const appliedTemplate = applyTemplate(template);
    
    // Apply theme
    setFormTheme(appliedTemplate.theme);
    setFormTemplateId(templateId);
    
    // Apply sample header image if available
    if (appliedTemplate.sample_header_image) {
      setHeaderImage(appliedTemplate.sample_header_image);
    }
    
    // If no fields exist, apply sample fields
    if (fields.length === 0) {
      setFields(appliedTemplate.sample_fields);
      toast.success(`Template "${template.name}" applied with sample fields!`);
    } else {
      // Only apply theme, don't replace existing fields
      toast.success(`Template "${template.name}" theme applied!`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-jakarta">Loading form builder...</p>
        </div>
      </div>
    );
  }

  if (showPreview) {
    return (
      <FormPreview
        fields={fields}
        basePrice={basePrice}
        campaignName={campaign?.name}
        onClose={() => setShowPreview(false)}
        fieldRules={fieldRules}
        pricingRules={pricingRules}
        pricingFormula={pricingFormula}
        campaignId={campaignId}
        theme={formTheme}
        headerImage={toAbsUrl(headerImage)}
      />
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Left Sidebar - Field Library */}
      <div className="w-64 border-r border-slate-200 bg-white p-4 overflow-y-auto">
        <div className="mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/campaigns/${campaignId}`)}
            data-testid="back-button"
            className="hover:bg-slate-100 rounded-lg w-full justify-start mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Event
          </Button>

          <h2 className="text-lg font-outfit font-semibold text-slate-900 mb-2">Field Library</h2>
          <p className="text-xs text-slate-600 font-jakarta mb-3">Click to add fields</p>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              type="text"
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
              placeholder="Search fields..."
              data-testid="field-library-search"
              className="h-9 pl-8 pr-8 text-sm rounded-lg border-slate-200 focus-visible:ring-primary"
            />
            {fieldSearch && (
              <button
                type="button"
                onClick={() => setFieldSearch('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {(() => {
            const q = fieldSearch.trim().toLowerCase();
            const matches = (f) =>
              !q ||
              f.label.toLowerCase().includes(q) ||
              f.type.toLowerCase().includes(q);

            const visibleCategories = FIELD_CATEGORIES
              .map(cat => ({
                ...cat,
                fields: FIELD_TYPES.filter(f => f.category === cat.id && matches(f))
              }))
              .filter(cat => cat.fields.length > 0);

            if (visibleCategories.length === 0) {
              return (
                <p className="text-xs text-slate-500 font-jakarta italic px-1">
                  No fields match "{fieldSearch}"
                </p>
              );
            }

            return visibleCategories.map(category => (
              <div key={category.id}>
                <h3 className="text-xs font-jakarta font-semibold text-slate-500 uppercase mb-2">
                  {category.label}
                </h3>
                <div className="space-y-2">
                  {category.fields.map(fieldType => {
                    const Icon = fieldType.icon;
                    return (
                      <button
                        key={fieldType.type}
                        onClick={() => addField(fieldType.type)}
                        data-testid={`add-field-${fieldType.type}`}
                        className="w-full flex items-center space-x-2 p-2 border border-slate-200 rounded-lg hover:border-primary hover:bg-primary-subtle transition-all duration-200 text-left"
                      >
                        <Icon className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-jakarta text-slate-700">{fieldType.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Center - Form Canvas with Tabs */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        <div className="max-w-5xl mx-auto p-6">
          {/* Header */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-card p-6 mb-6">
            <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
              {campaign?.name} - Form Builder
            </h1>
            <p className="text-sm text-slate-600 font-jakarta">Design your registration form with advanced features</p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-100 shadow-card p-2">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="design" className="font-jakarta">Design</TabsTrigger>
                <TabsTrigger value="fields" className="font-jakarta">Fields</TabsTrigger>
                <TabsTrigger value="formulas" className="font-jakarta">Formulas</TabsTrigger>
                <TabsTrigger value="rules" className="font-jakarta">Field Rules</TabsTrigger>
                <TabsTrigger value="pages" className="font-jakarta">Pages</TabsTrigger>
                <TabsTrigger value="integrations" className="font-jakarta">Integrations</TabsTrigger>
              </TabsList>
            </div>

            {/* Design Tab - Templates & Header Image */}
            <TabsContent value="design" className="space-y-6">
              {/* Header Image */}
              <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-outfit font-semibold text-slate-900">Form Header Image</h3>
                    <p className="text-sm text-slate-600 font-jakarta">Add a banner image at the top of your registration form</p>
                  </div>
                  {headerImage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHeaderImage('')}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
                
                {headerImage ? (
                  <div className="relative rounded-lg overflow-hidden border border-slate-200">
                    <img
                      src={toAbsUrl(headerImage)}
                      alt="Form header"
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setHeaderImage('')}
                        className="bg-white/90"
                      >
                        Change Image
                      </Button>
                    </div>
                  </div>
                ) : (
                  <ImageUploader
                    value={headerImage}
                    onChange={setHeaderImage}
                    label="Upload Header Image"
                    aspectRatio="banner"
                  />
                )}
              </Card>

              {/* Form Templates */}
              <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-outfit font-semibold text-slate-900">Form Templates</h3>
                  <p className="text-sm text-slate-600 font-jakarta">Choose a template to apply theme and sample fields</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {FORM_TEMPLATES.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleApplyTemplate(template.id)}
                      className={`group relative rounded-2xl cursor-pointer transition-all duration-300 overflow-hidden ${
                        formTemplateId === template.id 
                          ? 'ring-2 ring-primary ring-offset-2 shadow-xl scale-[1.02]' 
                          : 'hover:shadow-xl hover:scale-[1.02] border border-slate-200'
                      }`}
                    >
                      {/* Template Preview Card */}
                      <div 
                        className="h-40 relative overflow-hidden"
                        style={{ 
                          background: template.theme.page_background || template.theme.gradient || '#f8fafc'
                        }}
                      >
                        {/* Mini Header Image Preview */}
                        {template.sample_header_image && (
                          <div className="absolute top-3 left-3 right-3 h-10 rounded-md overflow-hidden shadow-sm">
                            <img 
                              src={template.sample_header_image} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        {/* Mini Form Preview */}
                        <div 
                          className="absolute left-3 right-3 rounded-lg shadow-lg p-3"
                          style={{ 
                            backgroundColor: template.theme.background_color,
                            top: template.sample_header_image ? '58px' : '12px',
                            bottom: '12px'
                          }}
                        >
                          {/* Mini form fields */}
                          <div 
                            className="h-2 w-16 rounded mb-2 opacity-60"
                            style={{ backgroundColor: template.theme.text_color }}
                          />
                          <div 
                            className="h-5 w-full rounded border mb-2"
                            style={{ borderColor: `${template.theme.text_color}30` }}
                          />
                          <div 
                            className="h-5 w-full rounded border mb-2"
                            style={{ borderColor: `${template.theme.text_color}30` }}
                          />
                          <div 
                            className="h-4 w-full rounded mt-auto"
                            style={{ backgroundColor: template.theme.primary_color }}
                          />
                        </div>
                        
                        {/* Selected Checkmark */}
                        {formTemplateId === template.id && (
                          <div className="absolute top-2 right-2 w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-lg">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                        
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      </div>
                      
                      {/* Template Info */}
                      <div className="p-4 bg-white">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{template.icon}</span>
                          <h4 className="font-jakarta font-semibold text-slate-900 text-sm">
                            {template.name}
                          </h4>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1 mb-3">
                          {template.description}
                        </p>
                        
                        {/* Color Palette */}
                        <div className="flex items-center gap-1.5">
                          <div 
                            className="w-5 h-5 rounded-full shadow-sm ring-1 ring-black/5"
                            style={{ backgroundColor: template.theme.primary_color }}
                            title="Primary"
                          />
                          <div 
                            className="w-5 h-5 rounded-full shadow-sm ring-1 ring-black/5"
                            style={{ backgroundColor: template.theme.secondary_color }}
                            title="Secondary"
                          />
                          <div 
                            className="w-5 h-5 rounded-full shadow-sm ring-1 ring-black/5"
                            style={{ backgroundColor: template.theme.accent_color }}
                            title="Accent"
                          />
                          <div 
                            className="w-5 h-5 rounded-full shadow-sm ring-1 ring-black/5"
                            style={{ backgroundColor: template.theme.page_background }}
                            title="Background"
                          />
                          <span className="text-[10px] text-slate-400 ml-auto font-jakarta">
                            {template.sample_fields.length} fields
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {fields.length > 0 && (
                  <div className="mt-5 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                    <p className="text-sm text-amber-800 font-jakarta">
                      <strong>💡 Tip:</strong> Applying a template will change the form's visual theme. 
                      To also apply sample fields, clear existing fields first or start with a new form.
                    </p>
                  </div>
                )}
              </Card>

              {/* Color Customization */}
              <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-outfit font-semibold text-slate-900">Customize Colors</h3>
                  <p className="text-sm text-slate-600 font-jakarta">Fine-tune the form's color scheme</p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {/* Page Background */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Page Background</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formTheme?.page_background || '#f8fafc'}
                        onChange={(e) => setFormTheme(prev => ({ 
                          ...prev, 
                          page_background: e.target.value,
                          gradient: null  // Clear gradient when setting solid color
                        }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input
                        value={formTheme?.page_background || '#f8fafc'}
                        onChange={(e) => setFormTheme(prev => ({ 
                          ...prev, 
                          page_background: e.target.value,
                          gradient: null
                        }))}
                        className="flex-1 h-10 text-xs font-mono"
                        placeholder="#f8fafc"
                      />
                    </div>
                  </div>

                  {/* Form Background */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Form Background</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formTheme?.background_color || '#ffffff'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, background_color: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input
                        value={formTheme?.background_color || '#ffffff'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, background_color: e.target.value }))}
                        className="flex-1 h-10 text-xs font-mono"
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>

                  {/* Text Color */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Text Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formTheme?.text_color || '#1e293b'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, text_color: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input
                        value={formTheme?.text_color || '#1e293b'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, text_color: e.target.value }))}
                        className="flex-1 h-10 text-xs font-mono"
                        placeholder="#1e293b"
                      />
                    </div>
                  </div>

                  {/* Primary Color */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Primary (Buttons)</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formTheme?.primary_color || '#7c3aed'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input
                        value={formTheme?.primary_color || '#7c3aed'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="flex-1 h-10 text-xs font-mono"
                        placeholder="#7c3aed"
                      />
                    </div>
                  </div>

                  {/* Secondary Color */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Secondary</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formTheme?.secondary_color || '#14b8a6'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input
                        value={formTheme?.secondary_color || '#14b8a6'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="flex-1 h-10 text-xs font-mono"
                        placeholder="#14b8a6"
                      />
                    </div>
                  </div>

                  {/* Accent Color */}
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Accent</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formTheme?.accent_color || '#7c3aed'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, accent_color: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200"
                      />
                      <Input
                        value={formTheme?.accent_color || '#7c3aed'}
                        onChange={(e) => setFormTheme(prev => ({ ...prev, accent_color: e.target.value }))}
                        className="flex-1 h-10 text-xs font-mono"
                        placeholder="#7c3aed"
                      />
                    </div>
                  </div>
                </div>

                {/* Reset to Default */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormTheme({
                      primary_color: '#7c3aed',
                      secondary_color: '#14b8a6',
                      background_color: '#ffffff',
                      text_color: '#1e293b',
                      accent_color: '#7c3aed',
                      font_family: 'Plus Jakarta Sans',
                      card_style: 'solid',
                      gradient: null,
                      page_background: '#f8fafc'
                    })}
                    className="text-slate-600 hover:text-slate-900"
                  >
                    Reset to Default
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(true)}
                    className="border-primary text-primary hover:bg-primary-subtle"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Form
                  </Button>
                </div>
              </Card>

              {/* Live Theme Preview */}
              {formTheme && (
                <Card className="bg-white rounded-xl border border-slate-100 shadow-card overflow-hidden">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-lg font-outfit font-semibold text-slate-900">Live Preview</h3>
                    <span className="text-xs text-slate-500 font-jakarta">Changes update in real-time</span>
                  </div>
                  <div 
                    className="p-8 transition-all duration-500"
                    style={{ 
                      background: formTheme.page_background || formTheme.gradient || '#f8fafc',
                      minHeight: '320px'
                    }}
                  >
                    <div className="max-w-md mx-auto">
                      {/* Mini Header Image */}
                      {headerImage && (
                        <div className="mb-4 rounded-xl overflow-hidden shadow-lg">
                          <img src={toAbsUrl(headerImage)} alt="Header" className="w-full h-28 object-cover" />
                        </div>
                      )}
                      
                      {/* Mini Form Card */}
                      <div 
                        className="rounded-xl shadow-xl p-6 transition-all duration-300"
                        style={{ 
                          backgroundColor: formTheme.background_color || '#ffffff',
                          color: formTheme.text_color || '#1e293b'
                        }}
                      >
                        {/* Form Title */}
                        <h4 
                          className="font-bold text-lg mb-4 pb-3 border-b"
                          style={{ 
                            fontFamily: formTheme.font_family,
                            borderColor: `${formTheme.text_color}15`
                          }}
                        >
                          {campaign?.name || 'Event Registration'}
                        </h4>
                        
                        {/* Sample Fields */}
                        <div className="space-y-3 mb-5">
                          <div>
                            <label className="text-xs font-medium mb-1 block opacity-70">Full Name</label>
                            <div 
                              className="h-9 rounded-lg border px-3 flex items-center text-sm opacity-50"
                              style={{ borderColor: `${formTheme.text_color}25` }}
                            >
                              John Doe
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium mb-1 block opacity-70">Email Address</label>
                            <div 
                              className="h-9 rounded-lg border px-3 flex items-center text-sm opacity-50"
                              style={{ borderColor: `${formTheme.text_color}25` }}
                            >
                              john@example.com
                            </div>
                          </div>
                        </div>
                        
                        {/* Price Display */}
                        <div 
                          className="flex items-center justify-between py-3 mb-4 border-t border-b"
                          style={{ borderColor: `${formTheme.text_color}15` }}
                        >
                          <span className="font-medium">Total Amount</span>
                          <span 
                            className="font-bold text-lg"
                            style={{ color: formTheme.primary_color }}
                          >
                            ₹{basePrice || '0'}.00
                          </span>
                        </div>
                        
                        {/* Submit Button */}
                        <button 
                          className="w-full py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-200 hover:opacity-90"
                          style={{ backgroundColor: formTheme.primary_color }}
                        >
                          Register & Pay
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Fields Tab */}
            <TabsContent value="fields" className="space-y-6">
              {/* Base Price */}
              <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
                <label className="text-sm font-jakarta font-medium text-slate-700 mb-2 block">
                  Base Registration Price (₹)
                </label>
                <input
                  type="number"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  placeholder="0"
                  data-testid="base-price-input"
                  className="h-11 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-all duration-200"
                />
              </Card>

              {/* Form Fields */}
              <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-6">
                {fields.length === 0 ? (
                  <div className="text-center py-16">
                    <Plus className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-jakarta mb-2">No fields added yet</p>
                    <p className="text-sm text-slate-500 font-jakarta">
                      Click on fields from the left sidebar to add them
                    </p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={fields.map(f => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {fields.map((field) => (
                          <SortableFieldItem
                            key={field.id}
                            field={field}
                            isSelected={selectedField?.id === field.id}
                            onSelect={() => setSelectedField(field)}
                            onDelete={() => deleteField(field.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </Card>
            </TabsContent>

            {/* Formulas Tab */}
            <TabsContent value="formulas">
              <FormulaBuilder 
                campaignId={campaignId}
                fields={fields}
                pricingRules={pricingRules}
                pricingFormula={pricingFormula}
                onSave={async (formulas) => {
                  setPricingRules(formulas.pricing_rules);
                  setPricingFormula(formulas.pricing_formula);
                  
                  // Save to database immediately
                  setSaving(true);
                  try {
                    const sanitizedFields = sanitizeFields(fields);
                    await api.put(`/campaigns/${campaignId}/form`, {
                      fields: sanitizedFields,
                      base_price: parseFloat(basePrice) || 0,
                      pricing_rules: formulas.pricing_rules,
                      pricing_formula: formulas.pricing_formula,
                      field_rules: fieldRules,
                      page_breaks: pageBreaks,
                      page_rules: pageRules,
                      integrations: integrations,
                      header_image: headerImage || null,
                      form_template_id: formTemplateId || null,
                      theme: formTheme
                    });
                    toast.success('Formulas saved to database!');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to save formulas');
                  } finally {
                    setSaving(false);
                  }
                }}
              />
            </TabsContent>

            {/* Field Rules Tab */}
            <TabsContent value="rules">
              <FieldRules 
                fields={fields}
                fieldRules={fieldRules}
                onUpdate={setFieldRules}
              />
            </TabsContent>

            {/* Pages Tab */}
            <TabsContent value="pages">
              <PageRules 
                fields={fields}
                pageBreaks={pageBreaks}
                pageRules={pageRules}
                onUpdateBreaks={setPageBreaks}
                onUpdateRules={setPageRules}
              />
            </TabsContent>

            {/* Integrations Tab */}
            <TabsContent value="integrations">
              <Integrations 
                integrations={integrations}
                onUpdate={setIntegrations}
              />
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              data-testid="preview-form-button"
              disabled={fields.length === 0}
              className="border-slate-200 hover:bg-slate-50 font-jakarta rounded-lg"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview Form
            </Button>
            <Button
              onClick={saveForm}
              disabled={saving}
              data-testid="save-form-button"
              className="bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg shadow-lg shadow-primary/20 transition-all duration-200 active:scale-95"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Form'}
            </Button>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Field Editor */}
      {selectedField && activeTab === 'fields' && (
        <div className="w-80 border-l border-slate-200 bg-white overflow-y-auto">
          <FieldEditor
            field={selectedField}
            onUpdate={updateField}
            onClose={() => setSelectedField(null)}
          />
        </div>
      )}
    </div>
  );
};

export default FormBuilder;
